import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Fire, Target, Medal, Crown, TrendUp, TrendDown, Eye, Sparkle, ArrowsClockwise,
} from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  Fire, Target, Medal, Crown, TrendUp, TrendDown, Eye, Sparkle,
};

const TYPE_STYLES = {
  success:   { bg: "bg-chart-3/10",      border: "border-chart-3/30",    text: "text-chart-3",    label: "Win" },
  milestone: { bg: "bg-secondary/15",    border: "border-secondary/40",  text: "text-secondary",  label: "Milestone" },
  warning:   { bg: "bg-destructive/10",  border: "border-destructive/30",text: "text-destructive",label: "Heads up" },
  info:      { bg: "bg-muted/30",        border: "border-border",        text: "text-muted-foreground", label: "Info" },
  insight:   { bg: "bg-primary/10",      border: "border-primary/30",    text: "text-primary",    label: "Insight" },
};

/**
 * Smart Alerts card — shows up to 3 contextual, rotating messages tailored to
 * the signed-in player's state (performance streak, ranking jump, qualification,
 * competitive context, next-opponent scouting).
 *
 * Requires the user to have claimed a profile — otherwise the card is hidden.
 */
export default function SmartAlertsCard() {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reason, setReason] = useState(null);

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/dashboard/smart-alerts`, { withCredentials: true });
      setAlerts(res.data?.alerts || []);
      setReason(res.data?.reason || null);
    } catch {
      setAlerts([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (user) fetchAlerts();
    else { setLoading(false); setAlerts([]); }
  }, [user, fetchAlerts]);

  if (!user) return null;
  if (!loading && !alerts.length && !reason) return null;

  return (
    <Card className="bg-card border-border" data-testid="smart-alerts-card">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          <Sparkle className="w-5 h-5 text-primary" weight="fill" />
          Smart Alerts
        </CardTitle>
        <Button
          size="sm" variant="ghost" className="h-7 px-2 text-muted-foreground"
          onClick={fetchAlerts}
          disabled={loading}
          title="Refresh alerts"
          data-testid="refresh-smart-alerts"
        >
          <ArrowsClockwise className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </CardHeader>
      <CardContent>
        {reason && !alerts.length && (
          <p className="text-sm text-muted-foreground text-center py-4" data-testid="smart-alerts-empty">
            {reason === "Profile not claimed"
              ? "Claim your player profile to unlock personalised insights."
              : reason}
          </p>
        )}

        {!reason && !alerts.length && !loading && (
          <p className="text-sm text-muted-foreground text-center py-4">
            No new insights right now — check back after your next match.
          </p>
        )}

        <div className="space-y-2">
          {alerts.map((a, idx) => {
            const Icon = ICON_MAP[a.icon] || Sparkle;
            const styles = TYPE_STYLES[a.type] || TYPE_STYLES.info;
            return (
              <div
                key={`${a.engine}-${a.template_id}-${idx}`}
                className={`p-3 rounded-lg border ${styles.bg} ${styles.border}`}
                data-testid={`smart-alert-${a.engine}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-md bg-background/40 ${styles.text} shrink-0`}>
                    <Icon className="w-4 h-4" weight="fill" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <p className={`font-heading font-semibold text-sm ${styles.text}`}>
                        {a.title}
                      </p>
                      <Badge variant="outline" className="text-[9px] capitalize border-current opacity-60">
                        {styles.label}
                      </Badge>
                      {a.priority === "high" && (
                        <Badge variant="outline" className="text-[9px] border-secondary/40 text-secondary">
                          Priority
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm leading-snug">{a.message}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
